/**
 * Payout schedule (owner, 2026-10-19): at the end of each month, what Kareem
 * owes the household is scheduled to be paid by the end of the next month.
 * The numbers come from the v2_settlement_schedule contract; this file only
 * shapes and words them, so Home and Settlements say the same thing.
 */

// 'YYYY-MM-DD' → "Oct 31" / "October". Dates are calendar days, so render in
// UTC (a date-only string parses as UTC midnight).
const day = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : '');
const monthName = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' }) : '');
const nextDay = (d) => {
  const t = new Date(d);
  t.setUTCDate(t.getUTCDate() + 1);
  return t.toISOString().slice(0, 10);
};

export function shapeSchedule(row) {
  if (!row || typeof row !== 'object') return null;
  const n = (k) => Number(row[k] || 0);
  const statementCredit = n('statementCredit');
  const paidSince = n('paidSinceStatement');
  const due = n('due');
  return {
    due,
    overdue: n('overdue'),
    building: n('building'),
    statementCredit,
    paidSinceStatement: paidSince,
    // The live credit came in under last month's balance (this month's
    // activity netted it down), so less than the statement is due.
    nettedDown: due < statementCredit - paidSince - 0.005,
    dueBy: row.dueBy,
    dueByLabel: day(row.dueBy),
    buildingDueByLabel: day(row.buildingDueBy),
    overdueSinceLabel: day(row.overdueSince),
    statementMonthLabel: monthName(row.statementEnd),
    openMonthLabel: row.statementEnd ? monthName(nextDay(row.statementEnd)) : '',
  };
}

// One row per date money is due to the investor: last month's balance by the
// end of this month, then this month's so far by the end of the next. `fmt`
// is the screen's money formatter. Empty when nothing is scheduled.
export function scheduleRows(s, fmt) {
  if (!s) return [];
  const rows = [];
  if (s.due >= 0.5) {
    const overdue = s.overdue >= 0.5;
    rows.push({
      label: `Paid to you by ${s.dueByLabel}`,
      amount: s.due,
      overdue,
      sub: overdue
        ? `incl. ${fmt(s.overdue)} that was due ${s.overdueSinceLabel}`
        : s.nettedDown
          ? `your ${s.statementMonthLabel} balance, net of ${s.openMonthLabel} so far`
          : `your balance at the end of ${s.statementMonthLabel}`,
    });
  }
  if (s.building >= 0.5) {
    rows.push({
      label: `${rows.length ? 'Then' : 'Paid to you'} by ${s.buildingDueByLabel}`,
      amount: s.building,
      overdue: false,
      sub: `${s.openMonthLabel}'s balance so far — set when the month closes`,
    });
  }
  return rows;
}

// The same schedule as one tile: the first date, with the next folded into
// its sub-line. Null when nothing is scheduled.
export function scheduleCopy(s, fmt) {
  const [first, next] = scheduleRows(s, fmt);
  if (!first) return null;
  return { ...first, sub: next ? `${first.sub} · ${fmt(next.amount)} more by ${s.buildingDueByLabel}` : first.sub };
}

// The "− your share" term of the who-owes-whom equation. Past the hurdle the
// investor keeps their side of the split, and the equation only sums with it.
// Each term renders rounded to the cent, so the share is the remainder that
// makes the line add up exactly (within a cent of the contract's investorNet).
// 0 below the hurdle, where there is no share.
export function equationShare({ investorNet, rewards, costs, held, paidIn = 0, paidOut = 0, waived = 0, result }) {
  if (!(Number(investorNet) > 0.005)) return 0;
  const r2 = (x) => Math.round(Number(x || 0) * 100) / 100;
  return r2(r2(rewards) - r2(costs) - r2(held) - r2(paidIn) + r2(paidOut) - r2(waived) - r2(result));
}
